import { useEffect, useCallback } from 'react';
import { registerNotificationHandler, ErrorSeverity } from '../../utils/errorHandling';
import notificationService from '../../services/notificationService';

// ============================================================================
// GLOBAL ERROR HANDLER COMPONENT
// ============================================================================
// Sets up global error handlers for:
// 1. Unhandled promise rejections (async errors)
// 2. Uncaught JavaScript errors (sync errors in event handlers)
// 3. Connects error handling utilities with notification service
// 4. Suppresses React dev overlay for handled errors

interface GlobalErrorHandlerProps {
  children: React.ReactNode;
  /** Enable/disable handling of unhandled promise rejections */
  handleUnhandledRejections?: boolean;
  /** Enable/disable handling of uncaught errors */
  handleUncaughtErrors?: boolean;
  /** Custom callback for errors */
  onError?: (error: Error | string, source?: string) => void;
  /** Whether to show notifications for global errors */
  showNotifications?: boolean;
  /** Whether to log errors to console */
  logErrors?: boolean;
}

/**
 * Map ErrorSeverity to notification type
 */
const severityToNotificationType = (
  severity: ErrorSeverity
): 'success' | 'info' | 'warning' | 'error' => {
  switch (severity) {
    case ErrorSeverity.INFO:
      return 'info';
    case ErrorSeverity.WARNING:
      return 'warning';
    case ErrorSeverity.ERROR:
    case ErrorSeverity.CRITICAL:
      return 'error';
    default:
      return 'error';
  }
};

/**
 * Global Error Handler Component
 *
 * This component sets up global error handlers when mounted and cleans them up
 * when unmounted. It should be placed near the root of your application.
 *
 * Features:
 * - Catches ALL unhandled errors automatically (no try-catch needed)
 * - Catches unhandled promise rejections
 * - Catches uncaught JavaScript errors (including event handlers)
 * - Shows user-friendly notifications via snackbar
 * - Suppresses React's ugly error overlay in development
 * - Integrates with your existing notification service
 *
 * @example
 * // In your App.tsx or index.tsx
 * <GlobalErrorHandler>
 *   <App />
 * </GlobalErrorHandler>
 */
export const GlobalErrorHandler: React.FC<GlobalErrorHandlerProps> = ({
  children,
  handleUnhandledRejections = true,
  handleUncaughtErrors = true,
  onError,
  showNotifications = true,
  logErrors = true,
}) => {
  /**
   * Notification handler that connects errorHandling.ts with notificationService
   */
  const notificationHandler = useCallback(
    (message: string, severity: ErrorSeverity, details?: any) => {
      if (!showNotifications) return;

      const type = severityToNotificationType(severity);
      const title =
        severity === ErrorSeverity.CRITICAL
          ? 'Critical Error'
          : severity === ErrorSeverity.ERROR
          ? 'Error'
          : severity === ErrorSeverity.WARNING
          ? 'Warning'
          : 'Info';

      notificationService.notify({
        type,
        title,
        message,
        autoClose: severity === ErrorSeverity.INFO || severity === ErrorSeverity.WARNING,
      });

      // Log details if available and logging is enabled
      if (logErrors && details) {
        console.debug('[GlobalErrorHandler] Error details:', details);
      }
    },
    [showNotifications, logErrors]
  );

  /**
   * Handle unhandled promise rejections (async errors)
   */
  const handleUnhandledRejection = useCallback(
    (event: PromiseRejectionEvent) => {
      // Prevent default browser error logging
      event.preventDefault();

      const error = event.reason;

      if (logErrors) {
        console.error('[GlobalErrorHandler] Unhandled Promise Rejection:', error);
      }

      // Show notification
      if (showNotifications) {
        const message =
          error instanceof Error
            ? error.message || 'An unexpected error occurred'
            : String(error) || 'An unexpected error occurred';

        notificationService.error('Error', message);
      }

      // Call custom error handler if provided
      if (onError) {
        onError(error instanceof Error ? error : new Error(String(error)), 'unhandledrejection');
      }
    },
    [logErrors, showNotifications, onError]
  );

  /**
   * Handle uncaught JavaScript errors (sync errors in event handlers, etc.)
   */
  const handleError = useCallback(
    (event: ErrorEvent) => {
      // Skip resource load errors (img/script/link/etc.) — these bubble to window
      // in capture phase as plain Events with no message/error, and surfacing them
      // as a generic "An unexpected error occurred" toast is misleading.
      if (event.target && event.target !== window) {
        return;
      }

      // Prevent React's error overlay from showing
      event.preventDefault();

      // Skip cross-origin script errors (no useful info)
      if (event.message === 'Script error.') {
        return;
      }

      if (logErrors) {
        console.error('[GlobalErrorHandler] Uncaught Error:', {
          message: event.message,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          error: event.error,
        });
      }

      // Show notification
      if (showNotifications) {
        const message = event.message || 'An unexpected error occurred. Please try again.';
        notificationService.error('Error', message);
      }

      // Call custom error handler if provided
      if (onError) {
        onError(event.error || new Error(event.message), 'error');
      }

      // Return true to prevent the error from propagating
      return true;
    },
    [logErrors, showNotifications, onError]
  );

  useEffect(() => {
    // Register the notification handler for errorHandling.ts utilities
    registerNotificationHandler(notificationHandler);

    // Set up global error handlers with capture phase to catch errors first
    if (handleUnhandledRejections) {
      window.addEventListener('unhandledrejection', handleUnhandledRejection);
    }

    if (handleUncaughtErrors) {
      // Use capture phase to catch errors before React's error overlay
      window.addEventListener('error', handleError, true);
    }

    // Log that global error handling is active (development only)
    if (process.env.NODE_ENV === 'development') {
      console.info('[GlobalErrorHandler] Global error handling initialized');
    }

    // Cleanup on unmount
    return () => {
      if (handleUnhandledRejections) {
        window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      }

      if (handleUncaughtErrors) {
        window.removeEventListener('error', handleError, true);
      }

      // Clear the notification handler
      registerNotificationHandler(() => {});

      if (process.env.NODE_ENV === 'development') {
        console.info('[GlobalErrorHandler] Global error handling cleaned up');
      }
    };
  }, [
    notificationHandler,
    handleUnhandledRejection,
    handleError,
    handleUnhandledRejections,
    handleUncaughtErrors,
  ]);

  return <>{children}</>;
};

export default GlobalErrorHandler;
